import React, { useCallback, useEffect, useMemo } from 'react';
import ChevronLeft from '@uber/base-icons/chevron-left';
import ChevronRight from '@uber/base-icons/chevron-right';
import PlayerNext from '@uber/base-icons/player-next';
import PlayerPauseFilled from '@uber/base-icons/player-pause';
import PlayerPlayFilled from '@uber/base-icons/player-play-filled';
import PlayerPrevious from '@uber/base-icons/player-previous';
import { useStyletron } from 'baseui';
import { StatefulTooltip } from 'baseui/tooltip';
import useShortcut from '../../image/labelling/providers/hooks/use-shortcut';
import { handleShortcutRegistrationError } from '../../image/labelling/providers/utils/shortcut';
import { PLAYBACK_ACTIONS } from './constants';
import { IconButton } from './icon-button';
import type { PlaybackAction, PlaybackButtonsProps } from './types';
export const PlaybackButtons = ({
  isPlaying,
  onAction,
  disabled = false,
  iconSize = 12,
  buttonOverrides
}: PlaybackButtonsProps) => {
  const [css, theme] = useStyletron();
  const {
    registerHotKeys
  } = useShortcut();
  const handleAction = useCallback((action: PlaybackAction) => {
    if (!disabled) onAction(action);
  }, [onAction, disabled]);
  const shortcuts = useMemo(() => [{
    keys: ['Escape'],
    allowInEditable: true,
    onAction: () => {
      handleAction(isPlaying ? PLAYBACK_ACTIONS.PAUSE : PLAYBACK_ACTIONS.PLAY);
    }
  }, {
    keys: ['Shift', '_'],
    onAction: (e: KeyboardEvent) => {
      e.preventDefault();
      handleAction(PLAYBACK_ACTIONS.STEP_BACKWARD);
    }
  }, {
    keys: ['Shift', '+'],
    onAction: (e: KeyboardEvent) => {
      e.preventDefault();
      handleAction(PLAYBACK_ACTIONS.STEP_FORWARD);
    }
  }, {
    keys: ['Alt', 'ArrowLeft'],
    onAction: (e: KeyboardEvent) => {
      e.preventDefault();
      handleAction(PLAYBACK_ACTIONS.SKIP_TO_START);
    }
  }, {
    keys: ['Alt', 'ArrowRight'],
    onAction: (e: KeyboardEvent) => {
      e.preventDefault();
      handleAction(PLAYBACK_ACTIONS.SKIP_TO_END);
    }
  }], [handleAction, isPlaying]);
  useEffect(() => {
    const {
      errors,
      removeHotKeys
    } = registerHotKeys(shortcuts);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [shortcuts, registerHotKeys]);
  return <div className={css({
    display: 'flex',
    gap: theme.sizing.scale300
  })}>
      <StatefulTooltip content="Skip to start" placement="bottom" showArrow>
        <IconButton data-testid="skip-to-start" onClick={() => handleAction(PLAYBACK_ACTIONS.SKIP_TO_START)} disabled={disabled} overrides={buttonOverrides}>
          <PlayerPrevious size={iconSize} title="Skip to start" />
        </IconButton>
      </StatefulTooltip>

      <StatefulTooltip content="Step backward" placement="bottom" showArrow>
        <IconButton data-testid="step-backward" onClick={() => handleAction(PLAYBACK_ACTIONS.STEP_BACKWARD)} disabled={disabled} overrides={buttonOverrides}>
          <ChevronLeft size={iconSize} title="Step backward" />
        </IconButton>
      </StatefulTooltip>

      <StatefulTooltip content={isPlaying ? 'Pause' : 'Play'} placement="bottom" showArrow>
        <IconButton data-testid="play-pause" onClick={() => handleAction(isPlaying ? PLAYBACK_ACTIONS.PAUSE : PLAYBACK_ACTIONS.PLAY)} disabled={disabled} overrides={buttonOverrides}>
          {isPlaying ? <PlayerPauseFilled size={iconSize} title="Pause" /> : <PlayerPlayFilled size={iconSize} title="Play" />}
        </IconButton>
      </StatefulTooltip>

      <StatefulTooltip content="Step forward" placement="bottom" showArrow>
        <IconButton data-testid="step-forward" onClick={() => handleAction(PLAYBACK_ACTIONS.STEP_FORWARD)} disabled={disabled} overrides={buttonOverrides}>
          <ChevronRight size={iconSize} title="Step forward" />
        </IconButton>
      </StatefulTooltip>

      <StatefulTooltip content="Skip to end" placement="bottom" showArrow>
        <IconButton data-testid="skip-to-end" onClick={() => handleAction(PLAYBACK_ACTIONS.SKIP_TO_END)} disabled={disabled} overrides={buttonOverrides}>
          <PlayerNext size={iconSize} title="Skip to end" />
        </IconButton>
      </StatefulTooltip>
    </div>;
};